import React, { useEffect, useRef, useState } from 'react';

/**
 * PromptModal — replaces window.prompt() with an in-app modal.
 * onSubmit(value) is called with the trimmed string when the user confirms.
 * onClose() is called on cancel / escape.
 */
const PromptModal = ({
  isOpen,
  onClose,
  onSubmit,
  title = 'Enter value',
  message = '',
  defaultValue = '',
  placeholder = '',
  confirmText = 'Save',
  cancelText = 'Cancel',
  variant = 'info',
}) => {
  const [value, setValue] = useState(defaultValue || '');
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setValue(defaultValue || '');
      // Focus & select after mount
      const t = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select();
        }
      }, 60);
      return () => clearTimeout(t);
    }
  }, [isOpen, defaultValue]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const variantBtn = {
    info: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
    danger: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
    warning: 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500',
    success: 'bg-green-600 hover:bg-green-700 focus:ring-green-500',
    purple: 'bg-purple-600 hover:bg-purple-700 focus:ring-purple-500',
  }[variant] || 'bg-purple-600 hover:bg-purple-700 focus:ring-purple-500';

  const submit = () => {
    const trimmed = (value || '').trim();
    onSubmit(trimmed);
  };

  const handleKey = (e) => {
    if (e.key === 'Enter') submit();
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm"
      style={{ animation: 'fadeIn 0.2s ease-out' }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 transform transition-all"
        style={{ animation: 'scaleIn 0.2s ease-out' }}
        data-testid="prompt-modal"
      >
        <div className="p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-1">{title}</h3>
          {message && <p className="text-sm text-gray-600 mb-4">{message}</p>}

          <input
            ref={inputRef}
            data-testid="prompt-modal-input"
            type="text"
            value={value}
            onChange={e => setValue(e.target.value)}
            onKeyDown={handleKey}
            placeholder={placeholder}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />

          <div className="flex gap-3 mt-5">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-semibold hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
            >
              {cancelText}
            </button>
            <button
              data-testid="prompt-modal-submit"
              onClick={submit}
              className={`flex-1 px-4 py-2.5 rounded-lg text-white font-semibold focus:outline-none focus:ring-2 transition-colors ${variantBtn}`}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.95) } to { opacity: 1; transform: scale(1) } }
      `}</style>
    </div>
  );
};

export default PromptModal;
